import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { User, Save, CheckCircle, Lock, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProfilePage() {
  const { user, getAuthHeaders, refreshUser } = useAuth();
  const [form, setForm] = useState({
    name: '', phone: '', businessName: '', address: '',
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  // Sync form with user data when user loads/changes
  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || '',
        phone: user.phone || '',
        businessName: user.businessName || '',
        address: user.address || '',
      });
    }
  }, [user]);

  // Password change
  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [showPw, setShowPw] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState({ text: '', type: '' });

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMsg('');
    try {
      await axios.put(`${API}/user/profile`, form, { headers: getAuthHeaders() });
      await refreshUser();
      setMsg('Profile updated!');
      setTimeout(() => setMsg(''), 3000);
    } catch (err) {
      setMsg(err.response?.data?.detail || 'Failed to update');
    }
    setSaving(false);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwForm.newPassword !== pwForm.confirmPassword) {
      setPwMsg({ text: "Passwords don't match", type: 'error' });
      return;
    }
    setPwSaving(true);
    setPwMsg({ text: '', type: '' });
    try {
      await axios.put(`${API}/user/password`, {
        currentPassword: pwForm.currentPassword,
        newPassword: pwForm.newPassword,
      }, { headers: getAuthHeaders() });
      setPwMsg({ text: 'Password changed!', type: 'success' });
      setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => setPwMsg({ text: '', type: '' }), 3000);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setPwMsg({ text: typeof detail === 'string' ? detail : 'Failed to change password', type: 'error' });
    }
    setPwSaving(false);
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setPw = (k, v) => setPwForm(f => ({ ...f, [k]: v }));

  return (
    <div className="page-enter p-4 md:p-8 max-w-2xl mx-auto">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 bg-[#E23D28] rounded-full border-2 border-[#2A2A2A] flex items-center justify-center shadow-[2px_2px_0px_0px_rgba(42,42,42,1)]">
          <User className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-4xl sm:text-5xl text-[#2A2A2A]" style={{ fontFamily: "'Bebas Neue', cursive" }} data-testid="profile-title">
            My Profile
          </h1>
          <p className="text-sm text-[#2A2A2A]/60">{user?.email}</p>
        </div>
      </div>

      {/* Profile Info */}
      <div className="pizza-card p-6 mb-6" data-testid="profile-form">
        <h2 className="text-2xl text-[#2A2A2A] mb-4" style={{ fontFamily: "'Bebas Neue', cursive" }}>Business Info</h2>
        <form onSubmit={handleSaveProfile} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-[#2A2A2A]/60 mb-0.5 block" style={{ fontFamily: "'Bebas Neue', cursive" }}>Full Name</label>
              <input type="text" value={form.name} onChange={e => set('name', e.target.value)}
                className="pizza-input w-full" data-testid="profile-name" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#2A2A2A]/60 mb-0.5 block" style={{ fontFamily: "'Bebas Neue', cursive" }}>Business Name</label>
              <input type="text" value={form.businessName} onChange={e => set('businessName', e.target.value)}
                className="pizza-input w-full" data-testid="profile-business" />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#2A2A2A]/60 mb-0.5 block" style={{ fontFamily: "'Bebas Neue', cursive" }}>Address</label>
            <input type="text" value={form.address} onChange={e => set('address', e.target.value)}
              className="pizza-input w-full" data-testid="profile-address" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#2A2A2A]/60 mb-0.5 block" style={{ fontFamily: "'Bebas Neue', cursive" }}>Cell Phone</label>
            <input type="tel" value={form.phone} onChange={e => set('phone', e.target.value)}
              className="pizza-input w-full" data-testid="profile-phone" />
          </div>
          <div className="flex items-center gap-3">
            <button type="submit" disabled={saving} className="pizza-btn text-sm flex items-center gap-1" data-testid="profile-save-btn">
              <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save Changes'}
            </button>
            {msg && <span className="text-sm text-[#2E7D32] flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5" /> {msg}</span>}
          </div>
        </form>
      </div>

      {/* Change Password */}
      <div className="pizza-card p-6" data-testid="password-form">
        <h2 className="text-2xl text-[#2A2A2A] mb-4" style={{ fontFamily: "'Bebas Neue', cursive" }}>Change Password</h2>
        <form onSubmit={handleChangePassword} className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-[#2A2A2A]/60 mb-0.5 block" style={{ fontFamily: "'Bebas Neue', cursive" }}>Current Password</label>
            <input type={showPw ? 'text' : 'password'} value={pwForm.currentPassword}
              onChange={e => setPw('currentPassword', e.target.value)}
              className="pizza-input w-full" data-testid="pw-current" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-[#2A2A2A]/60 mb-0.5 block" style={{ fontFamily: "'Bebas Neue', cursive" }}>New Password</label>
              <div className="relative">
                <input type={showPw ? 'text' : 'password'} minLength={6} value={pwForm.newPassword}
                  onChange={e => setPw('newPassword', e.target.value)}
                  placeholder="At least 6 characters" className="pizza-input w-full pr-10" data-testid="pw-new" />
                <button type="button" onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#2A2A2A]/40">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#2A2A2A]/60 mb-0.5 block" style={{ fontFamily: "'Bebas Neue', cursive" }}>Confirm New Password</label>
              <input type={showPw ? 'text' : 'password'} value={pwForm.confirmPassword}
                onChange={e => setPw('confirmPassword', e.target.value)}
                className="pizza-input w-full" data-testid="pw-confirm" />
            </div>
          </div>
          {pwMsg.text && (
            <p className={`text-sm ${pwMsg.type === 'error' ? 'text-[#E23D28]' : 'text-[#2E7D32]'}`} data-testid="pw-msg">{pwMsg.text}</p>
          )}
          <button type="submit" disabled={pwSaving || !pwForm.currentPassword || !pwForm.newPassword || pwForm.newPassword.length < 6 || pwForm.newPassword !== pwForm.confirmPassword}
            className="pizza-btn-secondary text-sm flex items-center gap-1 disabled:opacity-50" data-testid="pw-change-btn">
            <Lock className="w-4 h-4" /> {pwSaving ? 'Changing...' : 'Change Password'}
          </button>
        </form>
      </div>
    </div>
  );
}
